/**
 * Convert a currency amount to the integer number of cents that
 * `order/public-status/cancel/` expects.
 *
 * !! This deliberately TRUNCATES rather than rounds. !!
 *
 * The backend rejects the request unless our value matches its own, and its
 * own is computed with a float truncation of the same shape
 * (getbarcart `order/serializers.py` CancelOrderSerializer.validate_amount):
 *
 *     total_amount = int(round(order.total_with_refund, 2) * 100)
 *     if total_amount != amount:
 *         raise serializers.ValidationError("Amount should be equal total amount")
 *
 * `Order.total` is a FloatField, so for ~4.6% of cent values the product lands
 * just under the integer: int(round(8.29, 2) * 100) == 828, not 829. Rounding
 * correctly here would send 829 and the cancellation would be rejected outright.
 *
 * Once the backend rounds the product instead of truncating it, this should
 * become `Math.round(...)` — see toCentsRounded below. Deploy the backend
 * first; it must accept the new value before the frontend starts sending it.
 *
 * @param {string|number} amount - currency amount, e.g. "8.29"
 * @returns {number} the amount in cents as the backend computes it, e.g. 828
 */
export const toCents = (amount) => Math.trunc(parseFloat(amount ?? 0) * 100);

/**
 * The correct conversion, for use once the backend no longer truncates.
 * Verified to match the exact cent value for every amount up to $2,000.
 *
 * @param {string|number} amount - currency amount, e.g. "8.29"
 * @returns {number} the amount in cents, e.g. 829
 */
export const toCentsRounded = (amount) => Math.round(parseFloat(amount ?? 0) * 100);

/**
 * The cents figure the cancel endpoint will accept for an order.
 *
 * `CancelOrderSerializer.validate_amount` compares against the order's
 * `total_with_refund`, which is net of discounts, while the payload's `total`
 * is not. Sending `total` had every discounted order refused with "Amount
 * should be equal total amount". The fallback covers a backend that does not
 * serve the field yet.
 */
export const cancellableAmount = (order) => toCents(order?.total_with_refund ?? order?.total);
